/**
 * Accessibility Manager
 * 
 * Provides comprehensive accessibility support for visual feedback,
 * including ARIA labels, screen reader announcements, and keyboard navigation
 * support for validation states and error messages.
 */

import { FieldType } from '../detection-patterns';

/**
 * ARIA role types for different elements
 */
export type AriaRole = 'alert' | 'status' | 'application' | 'region' | 'group';

/**
 * Accessibility configuration
 */
export interface AccessibilityConfig {
  /** Whether to enable screen reader announcements */
  enableAnnouncements?: boolean;
  /** Whether to enable ARIA attributes */
  enableAriaAttributes?: boolean;
  /** Whether to enable focus management */
  enableFocusManagement?: boolean;
  /** Whether to enable high contrast support */
  enableHighContrast?: boolean;
  /** Debounce delay for announcements (ms) */
  announcementDelay?: number;
}

/**
 * Screen reader announcement options
 */
export interface AnnouncementOptions {
  /** Priority level of announcement */
  priority?: 'polite' | 'assertive';
  /** Whether to interrupt current announcements */
  interrupt?: boolean;
  /** Delay before announcement (ms) */
  delay?: number;
  /** Whether to deduplicate similar announcements */
  deduplicate?: boolean;
}

/**
 * Default accessibility configuration
 */
const DEFAULT_ACCESSIBILITY_CONFIG: Required<AccessibilityConfig> = {
  enableAnnouncements: true,
  enableAriaAttributes: true,
  enableFocusManagement: true,
  enableHighContrast: true,
  announcementDelay: 500
};

/**
 * Accessibility manager for visual feedback
 */
export class AccessibilityManager {
  private config: Required<AccessibilityConfig>;
  private liveRegion: HTMLElement | null = null;
  private fieldDescriptions = new Map<HTMLElement, string>();
  private lastAnnouncements = new Map<HTMLElement, string>();
  private announcementTimeouts = new Map<HTMLElement, NodeJS.Timeout>();

  constructor(enabled: boolean = true, config: AccessibilityConfig = {}) {
    this.config = { ...DEFAULT_ACCESSIBILITY_CONFIG, ...config };
    
    if (enabled) {
      this.initialize();
    }
  }

  /**
   * Initialize accessibility features
   */
  public initialize(): void {
    this.createLiveRegion();
    this.setupHighContrastSupport();
    this.setupKeyboardSupport();
  }

  /**
   * Set up field for accessibility
   */
  public setupField(element: HTMLElement, fieldType: FieldType): void {
    if (!this.config.enableAriaAttributes) {
      return;
    }

    // Set basic ARIA attributes
    this.setFieldAriaAttributes(element, fieldType);
    
    // Add field description
    this.addFieldDescription(element, fieldType);
    
    // Set up focus management
    if (this.config.enableFocusManagement) {
      this.setupFieldFocusManagement(element);
    }
  }

  /**
   * Set up message element accessibility
   */
  public setupMessageElement(
    messageElement: HTMLElement,
    fieldElement: HTMLElement,
    type: 'error' | 'warning' | 'success'
  ): void {
    if (!this.config.enableAriaAttributes) {
      return;
    }

    // Set message ID for ARIA relationship
    const messageId = this.generateMessageId(fieldElement, type);
    messageElement.id = messageId;

    // Set ARIA role based on type
    messageElement.setAttribute('role', type === 'error' ? 'alert' : 'status');
    messageElement.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');

    // Link field to message
    this.linkFieldToMessage(fieldElement, messageId);

    // Make sure message is readable
    messageElement.setAttribute('aria-atomic', 'true');
  }

  /**
   * Announce validation error to screen readers
   */
  public announceValidationError(element: HTMLElement, errors: string[]): void {
    if (!this.config.enableAnnouncements || errors.length === 0) {
      return;
    }

    const message = this.formatErrorAnnouncement(errors);
    this.announce(element, message, {
      priority: 'assertive',
      interrupt: true
    });
  }

  /**
   * Announce validation success to screen readers
   */
  public announceValidationSuccess(element: HTMLElement): void {
    if (!this.config.enableAnnouncements) {
      return;
    }

    const fieldLabel = this.getFieldLabel(element);
    const message = `${fieldLabel} is now valid`;
    
    this.announce(element, message, {
      priority: 'polite',
      delay: this.config.announcementDelay
    });
  }

  /**
   * Announce warning to screen readers
   */
  public announceWarning(element: HTMLElement, warnings: string[]): void {
    if (!this.config.enableAnnouncements || warnings.length === 0) {
      return;
    }

    const message = this.formatWarningAnnouncement(warnings);
    this.announce(element, message, {
      priority: 'polite'
    });
  }

  /**
   * Announce field formatting changes
   */
  public announceFormatChange(element: HTMLElement, oldValue: string, newValue: string): void {
    if (!this.config.enableAnnouncements || oldValue === newValue) {
      return;
    }

    const fieldLabel = this.getFieldLabel(element);
    const message = `${fieldLabel} formatted to ${newValue}`;
    
    this.announce(element, message, {
      priority: 'polite',
      delay: this.config.announcementDelay,
      deduplicate: true
    });
  }

  /**
   * Clear validation state announcements
   */
  public clearValidationState(element: HTMLElement): void {
    // Clear ARIA attributes
    element.removeAttribute('aria-invalid');
    element.removeAttribute('aria-describedby');

    // Clear any pending announcements
    const timeout = this.announcementTimeouts.get(element);
    if (timeout) {
      clearTimeout(timeout);
      this.announcementTimeouts.delete(element);
    }

    // Clear last announcement
    this.lastAnnouncements.delete(element);
  }

  /**
   * Update configuration
   */
  public updateConfig(enabled: boolean, config?: AccessibilityConfig): void {
    if (!enabled) {
      this.cleanup();
      return;
    }

    if (config) {
      this.config = { ...this.config, ...config };
    }

    this.initialize();
  }

  /**
   * Clean up field accessibility
   */
  public cleanupField(element: HTMLElement): void {
    // Remove ARIA attributes
    element.removeAttribute('aria-invalid');
    element.removeAttribute('aria-describedby');
    element.removeAttribute('aria-label');

    // Clear timeouts and references
    const timeout = this.announcementTimeouts.get(element);
    if (timeout) {
      clearTimeout(timeout);
      this.announcementTimeouts.delete(element);
    }

    this.fieldDescriptions.delete(element);
    this.lastAnnouncements.delete(element);
  }

  /**
   * Clean up accessibility manager
   */
  public cleanup(): void {
    // Remove live region
    if (this.liveRegion && this.liveRegion.parentNode) {
      this.liveRegion.parentNode.removeChild(this.liveRegion);
      this.liveRegion = null;
    }

    // Clear all timeouts
    this.announcementTimeouts.forEach(timeout => clearTimeout(timeout));
    this.announcementTimeouts.clear();

    // Clear maps
    this.fieldDescriptions.clear();
    this.lastAnnouncements.clear();
  }

  /**
   * Create live region for announcements
   */
  private createLiveRegion(): void {
    if (!this.config.enableAnnouncements) {
      return;
    }

    this.liveRegion = document.createElement('div');
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.setAttribute('aria-atomic', 'true');
    this.liveRegion.setAttribute('aria-relevant', 'all');
    this.liveRegion.style.position = 'absolute';
    this.liveRegion.style.left = '-10000px';
    this.liveRegion.style.width = '1px';
    this.liveRegion.style.height = '1px';
    this.liveRegion.style.overflow = 'hidden';
    
    document.body.appendChild(this.liveRegion);
  }

  /**
   * Set up high contrast support
   */
  private setupHighContrastSupport(): void {
    if (!this.config.enableHighContrast) {
      return;
    }

    // Check for high contrast mode and add appropriate classes
    if (window.matchMedia) {
      const highContrastQuery = window.matchMedia('(prefers-contrast: high)');
      const updateHighContrast = () => {
        document.body.classList.toggle('high-contrast', highContrastQuery.matches);
      };

      updateHighContrast();
      highContrastQuery.addEventListener('change', updateHighContrast);
    }
  }

  /**
   * Set up keyboard support
   */
  private setupKeyboardSupport(): void {
    if (!this.config.enableFocusManagement) {
      return;
    }

    // Add keyboard event listeners for navigation
    document.addEventListener('keydown', this.handleKeyboardNavigation.bind(this));
  }

  /**
   * Set field ARIA attributes
   */
  private setFieldAriaAttributes(element: HTMLElement, fieldType: FieldType): void {
    // Set aria-label if not present
    if (!element.getAttribute('aria-label') && !element.getAttribute('aria-labelledby')) {
      const label = this.generateFieldLabel(fieldType);
      element.setAttribute('aria-label', label);
    }

    // Set autocomplete attribute based on field type
    const autocomplete = this.getAutocompleteAttribute(fieldType);
    if (autocomplete && element.tagName.toLowerCase() === 'input') {
      (element as HTMLInputElement).setAttribute('autocomplete', autocomplete);
    }

    // Set inputmode for mobile keyboards
    const inputmode = this.getInputMode(fieldType);
    if (inputmode) {
      element.setAttribute('inputmode', inputmode);
    }
  }

  /**
   * Add field description for screen readers
   */
  private addFieldDescription(element: HTMLElement, fieldType: FieldType): void {
    const description = this.generateFieldDescription(fieldType);
    this.fieldDescriptions.set(element, description);

    // Create description element if needed
    const descriptionId = this.generateDescriptionId(element);
    let descriptionElement = document.getElementById(descriptionId);
    
    if (!descriptionElement) {
      descriptionElement = document.createElement('div');
      descriptionElement.id = descriptionId;
      descriptionElement.className = 'sr-only';
      descriptionElement.textContent = description;
      
      // Position near field
      element.parentNode?.insertBefore(descriptionElement, element.nextSibling);
      
      // Link to field
      const existingDescribedBy = element.getAttribute('aria-describedby');
      const describedBy = existingDescribedBy ? 
        `${existingDescribedBy} ${descriptionId}` : 
        descriptionId;
      element.setAttribute('aria-describedby', describedBy);
    }
  }

  /**
   * Set up focus management for field
   */
  private setupFieldFocusManagement(element: HTMLElement): void {
    element.addEventListener('focus', () => {
      // Announce field purpose on focus if configured
      const description = this.fieldDescriptions.get(element);
      if (description) {
        this.announce(element, description, {
          priority: 'polite',
          delay: 1000 // Delay to avoid interfering with focus announcement
        });
      }
    });
  }

  /**
   * Handle keyboard navigation
   */
  private handleKeyboardNavigation(event: KeyboardEvent): void {
    // Handle Escape key to clear error announcements
    if (event.key === 'Escape' && event.target instanceof HTMLElement) {
      this.clearValidationState(event.target);
    }

    // Handle Enter key to re-announce current state
    if (event.key === 'Enter' && event.ctrlKey && event.target instanceof HTMLElement) {
      const lastAnnouncement = this.lastAnnouncements.get(event.target);
      if (lastAnnouncement) {
        this.announce(event.target, lastAnnouncement, { priority: 'assertive' });
      }
    }
  }

  /**
   * Make announcement to screen readers
   */
  private announce(
    element: HTMLElement,
    message: string,
    options: AnnouncementOptions = {}
  ): void {
    if (!this.liveRegion) {
      return;
    }

    const {
      priority = 'polite',
      interrupt = false,
      delay = 0,
      deduplicate = true
    } = options;

    // Check for duplicate announcements
    if (deduplicate && this.lastAnnouncements.get(element) === message) {
      return;
    }

    // Clear existing timeout
    const existingTimeout = this.announcementTimeouts.get(element);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Store announcement
    this.lastAnnouncements.set(element, message);

    // Make announcement
    const makeAnnouncement = () => {
      if (!this.liveRegion) return;

      this.liveRegion.setAttribute('aria-live', priority);
      
      if (interrupt) {
        this.liveRegion.textContent = '';
        // Force reflow
        this.liveRegion.offsetHeight;
      }
      
      this.liveRegion.textContent = message;
    };

    if (delay > 0) {
      const timeout = setTimeout(makeAnnouncement, delay);
      this.announcementTimeouts.set(element, timeout);
    } else {
      makeAnnouncement();
    }
  }

  /**
   * Generate message ID for field
   */
  private generateMessageId(element: HTMLElement, type: string): string {
    const elementId = element.id || `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return `${elementId}_${type}_message`;
  }

  /**
   * Generate description ID for field
   */
  private generateDescriptionId(element: HTMLElement): string {
    const elementId = element.id || `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return `${elementId}_description`;
  }

  /**
   * Link field to message via ARIA
   */
  private linkFieldToMessage(fieldElement: HTMLElement, messageId: string): void {
    const existingDescribedBy = fieldElement.getAttribute('aria-describedby');
    const describedBy = existingDescribedBy ? 
      `${existingDescribedBy} ${messageId}` : 
      messageId;
    fieldElement.setAttribute('aria-describedby', describedBy);
    fieldElement.setAttribute('aria-invalid', 'true');
  }

  /**
   * Format error announcement for screen readers
   */
  private formatErrorAnnouncement(errors: string[]): string {
    const fieldLabel = errors.length === 1 ? 'Error' : 'Errors';
    return `${fieldLabel}: ${errors.join('. ')}`;
  }

  /**
   * Format warning announcement for screen readers
   */
  private formatWarningAnnouncement(warnings: string[]): string {
    const fieldLabel = warnings.length === 1 ? 'Warning' : 'Warnings';
    return `${fieldLabel}: ${warnings.join('. ')}`;
  }

  /**
   * Get field label for announcements
   */
  private getFieldLabel(element: HTMLElement): string {
    // Try various methods to get field label
    const ariaLabel = element.getAttribute('aria-label');
    if (ariaLabel) return ariaLabel;

    const labelledBy = element.getAttribute('aria-labelledby');
    if (labelledBy) {
      const labelElement = document.getElementById(labelledBy);
      if (labelElement) return labelElement.textContent || '';
    }

    // Try to find associated label
    const id = element.id;
    if (id) {
      const labelElement = document.querySelector(`label[for="${id}"]`);
      if (labelElement) return labelElement.textContent || '';
    }

    // Try placeholder
    const placeholder = (element as HTMLInputElement).placeholder;
    if (placeholder) return placeholder;

    return 'Field';
  }

  /**
   * Generate field label based on type
   */
  private generateFieldLabel(fieldType: FieldType): string {
    const labels: Record<FieldType, string> = {
      [FieldType.PHONE]: 'Phone number',
      [FieldType.ZIP]: 'ZIP code',
      [FieldType.DATE_OF_BIRTH]: 'Date of birth',
      [FieldType.CURRENCY]: 'Amount',
      [FieldType.SSN]: 'Social Security Number',
      [FieldType.UNKNOWN]: 'Text field'
    };

    return labels[fieldType] || labels[FieldType.UNKNOWN];
  }

  /**
   * Generate field description based on type
   */
  private generateFieldDescription(fieldType: FieldType): string {
    const descriptions: Record<FieldType, string> = {
      [FieldType.PHONE]: 'Enter your 10-digit phone number. Formatting will be applied automatically.',
      [FieldType.ZIP]: 'Enter your 5-digit ZIP code.',
      [FieldType.DATE_OF_BIRTH]: 'Enter your date of birth in month, day, year format.',
      [FieldType.CURRENCY]: 'Enter a dollar amount. Commas and decimal points are optional.',
      [FieldType.SSN]: 'Enter your 9-digit Social Security Number. Formatting will be applied automatically.',
      [FieldType.UNKNOWN]: 'Enter the requested information.'
    };

    return descriptions[fieldType] || descriptions[FieldType.UNKNOWN];
  }

  /**
   * Get autocomplete attribute for field type
   */
  private getAutocompleteAttribute(fieldType: FieldType): string | null {
    const autocompleteMap: Partial<Record<FieldType, string>> = {
      [FieldType.PHONE]: 'tel',
      [FieldType.ZIP]: 'postal-code',
      [FieldType.DATE_OF_BIRTH]: 'bday'
    };

    return autocompleteMap[fieldType] || null;
  }

  /**
   * Get input mode for mobile keyboards
   */
  private getInputMode(fieldType: FieldType): string | null {
    const inputModeMap: Partial<Record<FieldType, string>> = {
      [FieldType.PHONE]: 'tel',
      [FieldType.ZIP]: 'numeric',
      [FieldType.DATE_OF_BIRTH]: 'numeric',
      [FieldType.CURRENCY]: 'decimal',
      [FieldType.SSN]: 'numeric'
    };

    return inputModeMap[fieldType] || null;
  }
}